import React, {
    createRef,
    useCallback,
    useEffect,
    useState,
    forwardRef,
} from 'react';
import {Grid, CircularProgress, Hidden} from '@material-ui/core';
import useResizeListener from 'hooks/useResizeListener';
import {Document, Page} from 'react-pdf/dist/esm/entry.webpack';
import {pdfjs} from 'react-pdf';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import ZoomOutMapIcon from '@material-ui/icons/ZoomOutMap';
import LaunchIcon from '@material-ui/icons/Launch';
import Show from 'components/utils/Show';
import ResponsiveButton from './ResponsiveButton';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = '/static/js/pdf.worker.min.js';

const PositionHelper = ({children, ratio = false}) => {
    let styles = {
        display: 'flex',
        justifyContent: 'center',
        margin: '5em',
    };

    if (ratio) {
        styles.margin = null;
        styles.marginTop = `${ratio}%`;
        styles.height = '0px';
    }

    return <div style={styles}>{children}</div>;
};

const PDFLoading = forwardRef((props, ref) => {
    let styles = {};
    let ratio = false;

    if (ref?.current?.dataset.context === 'cardMedia') {
        ratio = 56;
        styles = {
            position: 'relative',
            top: '-150px',
        };
    }

    return (
        <PositionHelper ratio={ratio}>
            <CircularProgress style={styles} />
        </PositionHelper>
    );
});

const PDFError = forwardRef((props, ref) => {
    let styles = {
        textAlign: 'center',
    };
    let ratio = false;

    if (ref?.current?.dataset.context === 'cardMedia') {
        ratio = 56;
        styles.position = 'relative';
        styles.top = '-150px';
    }

    return (
        <PositionHelper ratio={ratio}>
            <span style={styles}>
                There was an error loading your PDF
                <br />
                Please refresh to try again.
            </span>
        </PositionHelper>
    );
});

const useStyles = makeStyles(theme => ({
    center: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
    },
    panLimiter: {
        overflow: 'hidden',
    },
    container: {
        width: '100%',
    },
    pdfContainer: {
        touchAction: 'none',
        maxWidth: 'calc(100vw - 16px)',
        transform: 'translate(var(--pan-x), var(--pan-y))',
        margin: '1em 0',
        cursor: 'grab',
    },
    pdfHeight: {
        [theme.breakpoints.up('lg')]: {
            height: 'calc(100vh - 10.20em)',
            maxWidth: 'calc(50vw - 8.4em)',
        },
    }
}));

const initialPan = {x: 0, y: 0};

const Preview = forwardRef(
    (
        {file, allPages, spacing = 2, onClick, showControls, smallControls, height, noPan},
        ref
    ) => {
        const pageRef = createRef();
        const {registerResizeListener, unregisterResizeListener} =
            useResizeListener();
        const [pages, setPages] = useState(0);
        const [page, setPage] = useState(1);
        const [unmount, setUnmount] = useState(false);
        const theme = useTheme();
        const classes = useStyles();
        const [scale, setScale] = useState(10);
        const [clicked, setClicked] = useState(false);
        const [position, setPosition] = useState(initialPan);
        const [pan, setPan] = useState(initialPan);
        const [width, setWidth] = useState(
            ref.current?.clientWidth || window.innerWidth
        );
        const touchPoints =
            navigator.maxTouchPoints || navigator.msMaxTouchPoints;

        // If we resized down, unmount
        const forcePdfUnmount = useCallback((prevSize, size) => {
            if (prevSize.width > size.width) {
                setUnmount(true);
            }
        }, []);

        // Register the resize callback
        useEffect(() => {
            registerResizeListener('previewlist-resize', forcePdfUnmount);
            return () => {
                unregisterResizeListener('previewlist-resize');
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        // Remount after unmount
        useEffect(() => {
            if (unmount) {
                setUnmount(false);
            }
        }, [unmount]);

        const pdfLoadSuccess = ({numPages}) => {
            setPages(numPages);
        };

        const openFull = () => {
            window.open(file, '_blank');
        };

        const dragStart = e => {
            if (!touchPoints && !noPan) {
                setClicked(true);
                setPosition({
                    x: e.clientX,
                    y: e.clientY,
                });
            }
        };

        const dragEnd = e => {
            if (!touchPoints) {
                setClicked(false);
                setPosition(initialPan);
            }
        };

        const dragUpdate = e => {
            if (!touchPoints && clicked) {
                setPan(pan => ({
                    x: pan.x + e.movementX,
                    y: pan.y + e.movementY,
                }));
            }
        };

        const touchStart = e => {
            if (!noPan) {
                setClicked(true);
                setPosition({
                    x: e.touches[0].clientX,
                    y: e.touches[0].clientY,
                });
            }
        };

        const touchEnd = e => {
            if (clicked) {
                setClicked(false);
                setPosition(initialPan);
            }
        };

        const touchUpdate = e => {
            if (clicked) {
                setPan({
                    x: e.touches[0].clientX - position.x,
                    y: e.touches[0].clientY - position.y,
                });
            }
        };

        useEffect(() => {
            if (ref.current) {
                setWidth(ref.current.clientWidth);
            }
        }, [ref]);

        return unmount ? null : (
            <div onClick={onClick} className={classes.container}>
                <Document
                    file={file}
                    onLoadSuccess={pdfLoadSuccess}
                    loading={<PDFLoading ref={ref} />}
                    error={<PDFError ref={ref} />}
                    renderMode={'canvas'}
                    options={{
                        cMapUrl: '/static/cmaps/',
                        cMapPacked: true,
                    }}
                >
                    {allPages
                        ? Array.from(new Array(pages), (el, pageIndex) => (
                            <Page
                                key={`page_${pageIndex + 1}`}
                                pageIndex={pageIndex}
                                width={
                                    ref?.current?.clientWidth -
                                    spacing * theme.spacing()
                                }
                                height={height}
                                loading={<PDFLoading ref={ref} />}
                                error={<PDFError ref={ref} />}
                                renderTextLayer={true}
                                renderAnnotationLayer={true}
                            />
                        ))
                        : [
                            <Show condition={showControls} key='controls'>
                                <Grid
                                    container
                                    justifyContent='space-between'
                                    className={classes.controls}
                                >
                                    <Grid item>
                                        <ResponsiveButton
                                            aria-label='page back'
                                            onClick={() =>
                                                setPage(
                                                    page === 1 ? pages : page - 1
                                                )
                                            }
                                            breakpoint={smallControls ? 'xl' : null}
                                            startIcon={<ChevronLeftIcon />}
                                        >
                                            Previous Page
                                        </ResponsiveButton>
                                    </Grid>
                                    <Grid item>
                                        <ResponsiveButton
                                            aria-label='zoom out'
                                            onClick={() => {
                                                if (scale > 1) {
                                                    setScale(scale => scale - 2.5);
                                                }
                                            }}
                                        breakpoint={smallControls ? 'xl' : null}
                                            startIcon={<ZoomOutIcon />}
                                        ></ResponsiveButton>
                                    </Grid>
                                    <Show condition={!file?.startsWith('data')}>
                                        <Grid item>
                                            <ResponsiveButton
                                            onClick={openFull}
                                            breakpoint={smallControls ? 'xl' : null}
                                            startIcon={<LaunchIcon />}
                                        >
                                                <Hidden xsDown>
                                                    Open in new window
                                                </Hidden>
                                            </ResponsiveButton>
                                        </Grid>
                                    </Show>
                                    <Grid item>
                                        <ResponsiveButton
                                            aria-label='reset zoom'
                                            onClick={() => {
                                                setScale(10);
                                                setPan(initialPan);
                                            }}
                                        breakpoint={smallControls ? 'xl' : null}
                                            startIcon={<ZoomOutMapIcon />}
                                        >
                                            Reset Zoom
                                        </ResponsiveButton>
                                    </Grid>
                                    <Grid item>
                                        <ResponsiveButton
                                            aria-label='zoom in'
                                            onClick={() => {
                                                if (scale < 50) {
                                                    setScale(scale => scale + 2.5);
                                                }
                                            }}
                                        breakpoint={smallControls ? 'xl' : null}
                                            startIcon={<ZoomInIcon />}
                                        ></ResponsiveButton>
                                    </Grid>
                                    <Grid item>
                                        <ResponsiveButton
                                            aria-label='page forward'
                                            onClick={() =>
                                                setPage(
                                                    page === pages ? 1 : page + 1
                                                )
                                            }
                                        breakpoint={smallControls ? 'xl' : null}
                                            startIcon={<ChevronRightIcon />}
                                        >
                                            Next Page
                                        </ResponsiveButton>
                                    </Grid>
                                </Grid>
                            </Show>,
                            <div className={classes.panLimiter} key='canvasLimit'>
                                <div
                                    className={`${classes.pdfContainer} ${smallControls ? null : classes.pdfHeight}`}
                                    style={{
                                        '--pan-x': `${pan.x}px`,
                                        '--pan-y': `${pan.y}px`,
                                    }}
                                    onMouseDown={dragStart}
                                    onMouseUp={dragEnd}
                                    onMouseMove={dragUpdate}
                                    onMouseOut={e => dragEnd(e) && dragUpdate(e)}
                                    onDoubleClick={() =>
                                        setScale(scale => scale + 2.5)
                                    }
                                    onTouchStart={touchStart}
                                    onTouchEnd={touchEnd}
                                    onTouchMove={touchUpdate}
                                >
                                    <Page
                                        pageNumber={page}
                                        className={classes.center}
                                        width={width}
                                        scale={
                                            ref?.current?.dataset?.context ===
                                            'cardMedia'
                                                ? 0.75
                                                : ref?.current?.dataset
                                                    ?.context === 'modal'
                                                ? window.innerHeight /
                                                    window.innerWidth -
                                                0.2
                                                : scale / 10
                                        }
                                        loading={<PDFLoading ref={ref} />}
                                        error={<PDFError ref={ref} />}
                                        renderTextLayer={true}
                                        renderAnnotationLayer={true}
                                        renderInteractiveForms={true}
                                        inputRef={pageRef}
                                    />
                                </div>
                            </div>,
                        ]}
                </Document>
            </div>
        );
    }
);

export default Preview;
